import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCurrency } from "../contexts/CurrencyContext";
import {
  LayoutDashboard, FileText, Globe, Newspaper, Bell,
  LogOut, Building2, ChevronLeft, Menu, ArrowLeftRight, LifeBuoy, Calculator, ShieldCheck,
  MessageSquarePlus, ScanSearch,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Sidebar nav grouped into 3 sections per UX brief Feb 28, 2026.
// Functionality, routing, and active states are untouched — this is purely
// a visual restructure with thin dividers and 10px uppercase group labels.
const navGroups = [
  {
    label: "Portfolio",
    items: [
      { to: "/portfolio", icon: LayoutDashboard, label: "Portfolio" },
      { to: "/deals", icon: Calculator, label: "Deal Analyzer" },
      { to: "/fx", icon: ArrowLeftRight, label: "FX Engine" },
    ],
  },
  {
    label: "Compliance",
    items: [
      { to: "/contract-radar", icon: ScanSearch, label: "Contract Radar" },
      { to: "/documents", icon: FileText, label: "Document Vault" },
      { to: "/mobility", icon: Globe, label: "Residency Tracker" },
      { to: "/news", icon: Newspaper, label: "Sentinel" },
    ],
  },
  {
    label: "Tools",
    items: [
      { to: "/notifications", icon: Bell, label: "Alerts" },
      { to: "/support", icon: LifeBuoy, label: "Concierge" },
    ],
  },
];

// Admin-only nav entries — appended after the main nav for admin users only.
const adminNavItems = [
  { to: "/admin", icon: ShieldCheck, label: "Admin" },
];

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { mode, setMode, homeCurrencies } = useCurrency();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const SidebarContent = ({ isMobile = false }) => (
    <div className="flex flex-col h-full" data-testid="sidebar">
      {/* Logo */}
      <div className="px-5 py-6 flex items-center gap-3">
        <NavLink to="/portfolio" onClick={() => isMobile && setMobileOpen(false)} data-testid="sidebar-logo-home" aria-label="Go to Portfolio home">
          {collapsed && !isMobile ? (
            <img src="/equia-logo.png" alt="EQUIA" className="h-10 w-10 object-contain object-left flex-shrink-0" style={{ objectPosition: "0 50%" }} />
          ) : (
            <img src="/equia-logo.png" alt="EQUIA Global Property" className="h-14 w-auto" data-testid="equia-logo" />
          )}
        </NavLink>
      </div>

      {/* Global Home-Currency Switch · EUR / AED / GBP */}
      {(!collapsed || isMobile) && (
        <div className="px-4 mt-4 mb-2">
          <p className="text-[10px] tracking-[0.12em] uppercase text-white/30 mb-1.5">Currency</p>
          <div
            className="flex items-stretch bg-white/[0.02] border border-white/[0.10] p-0.5"
            data-testid="global-currency-switch"
          >
            {homeCurrencies.map((code) => (
              <button
                key={code}
                onClick={() => setMode(code)}
                data-testid={`global-currency-${code.toLowerCase()}`}
                className={`flex-1 text-[10px] font-mono tracking-[0.1em] py-1.5 transition-all ${
                  mode === code
                    ? "bg-[#D4AF37] text-black"
                    : "text-white/40 hover:text-white"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
        </div>
      )}
      {collapsed && !isMobile && (
        <div className="px-3 mt-4 mb-2">
          <button
            onClick={() => {
              const i = homeCurrencies.indexOf(mode);
              setMode(homeCurrencies[(i + 1) % homeCurrencies.length]);
            }}
            data-testid="global-currency-toggle-collapsed"
            className="w-full flex items-center justify-center py-2 bg-white/[0.03] border border-white/[0.10] hover:border-[#D4AF37]/30 transition-all"
            title={`Home currency: ${mode} (click to cycle)`}
          >
            <span className="text-[10px] font-mono text-[#D4AF37]">{mode}</span>
          </button>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-3 mt-4">
        {navGroups.map((group, groupIdx) => (
          <div key={group.label}>
            {/* Group label — hidden when sidebar is collapsed on desktop.
                Spec: 10px uppercase, 0.12em tracking, #444 colour, 24px top
                margin (6px on first group), 6px bottom margin, 16px left pad.
                Each group except the first is preceded by a hairline divider
                (#1e1e1e, 0.5px) for visual separation. */}
            {(!collapsed || isMobile) && (
              <>
                {groupIdx > 0 && (
                  <div
                    aria-hidden="true"
                    className="mx-3"
                    style={{ height: "0.5px", backgroundColor: "#1e1e1e", marginTop: "24px" }}
                  />
                )}
                <p
                  data-testid={`sidebar-group-${group.label.toLowerCase()}`}
                  style={{
                    fontSize: "10px",
                    textTransform: "uppercase",
                    letterSpacing: "0.12em",
                    color: "#444",
                    marginTop: groupIdx === 0 ? "0" : "8px",
                    marginBottom: "6px",
                    paddingLeft: "16px",
                  }}
                >
                  {group.label}
                </p>
              </>
            )}
            {/* When the sidebar is collapsed, keep visual separation between
                groups via a hairline only — no text label. */}
            {collapsed && !isMobile && groupIdx > 0 && (
              <div aria-hidden="true" className="h-px mx-3 my-3 bg-white/[0.06]" />
            )}

            <div className="space-y-1">
              {group.items.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => isMobile && setMobileOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-sm transition-all group ${
                      isActive
                        ? "bg-white/5 text-white border-l-2 border-[#D4AF37]"
                        : "text-white/40 hover:text-white hover:bg-white/[0.03] border-l-2 border-transparent"
                    }`
                  }
                  data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                  {(!collapsed || isMobile) && <span className="font-sans truncate">{label}</span>}
                </NavLink>
              ))}
            </div>
          </div>
        ))}

        {/* Beta Feedback — gold-tinted standalone entry. Slated for removal post-beta. */}
        <NavLink
          to="/feedback"
          onClick={() => isMobile && setMobileOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 text-sm transition-all group mt-3 ${
              isActive
                ? "bg-[#D4AF37]/[0.08] text-[#D4AF37] border-l-2 border-[#D4AF37]"
                : "text-[#D4AF37]/80 hover:text-[#D4AF37] hover:bg-[#D4AF37]/[0.05] border-l-2 border-transparent"
            }`
          }
          data-testid="nav-feedback"
          title="Beta Feedback"
        >
          <MessageSquarePlus className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
          {(!collapsed || isMobile) && (
            <span className="font-sans truncate flex items-center gap-2">
              Feedback
              <span className="text-[8px] tracking-[0.2em] uppercase px-1.5 py-0.5 bg-[#D4AF37]/15 text-[#D4AF37] font-medium">
                Beta
              </span>
            </span>
          )}
        </NavLink>

        {/* Admin-only nav entries. Separated by a thin divider + eyebrow label.
            The route + every API endpoint also server-checks role=admin, so
            hiding this is purely a UX nicety, not the actual security boundary. */}
        {user?.role === "admin" && (
          <>
            <div className="pt-4 pb-2 px-3">
              {(!collapsed || isMobile) && (
                <p className="text-[9px] tracking-[0.2em] uppercase text-[#D4AF37]/50">Admin</p>
              )}
              {collapsed && !isMobile && <div className="h-px bg-white/[0.06]" />}
            </div>
            {adminNavItems.map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => isMobile && setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-all group ${
                    isActive
                      ? "bg-[#D4AF37]/[0.05] text-[#D4AF37] border-l-2 border-[#D4AF37]"
                      : "text-[#D4AF37]/60 hover:text-[#D4AF37] hover:bg-[#D4AF37]/[0.03] border-l-2 border-transparent"
                  }`
                }
                data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                {(!collapsed || isMobile) && <span className="font-sans truncate">{label}</span>}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* User */}
      <div className="px-3 pb-4 mt-auto border-t border-white/5 pt-4">
        <NavLink
          to="/settings"
          onClick={() => isMobile && setMobileOpen(false)}
          data-testid="nav-profile"
          title="Profile & Settings"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 mb-2 transition-all group ${
              isActive
                ? "bg-white/5 border-l-2 border-[#D4AF37] -ml-3 pl-[10px]"
                : "border-l-2 border-transparent hover:bg-white/[0.03] -ml-3 pl-[10px]"
            }`
          }
        >
          <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 group-hover:bg-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] text-xs font-medium flex-shrink-0 transition-colors">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          {(!collapsed || isMobile) && (
            <div className="overflow-hidden">
              <p className="text-white text-sm truncate group-hover:text-[#D4AF37] transition-colors">{user?.name || "User"}</p>
              <p className="text-white/30 text-[10px] truncate">{user?.email}</p>
            </div>
          )}
        </NavLink>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="flex items-center gap-3 px-3 py-2 text-white/30 hover:text-red-400 text-sm transition-colors w-full"
        >
          <LogOut className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
          {(!collapsed || isMobile) && <span>Sign Out</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#050505] overflow-hidden">
      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex flex-col border-r border-white/[0.10] bg-[#050505] transition-all duration-300 ${
          collapsed ? "w-[72px]" : "w-[240px]"
        }`}
      >
        <SidebarContent />
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute bottom-20 left-[calc(var(--sidebar-w)-12px)] z-10 w-6 h-6 bg-[#121212] border border-white/10 flex items-center justify-center text-white/40 hover:text-white transition-colors"
          style={{ "--sidebar-w": collapsed ? "72px" : "240px", left: collapsed ? "60px" : "228px" }}
          data-testid="collapse-sidebar-btn"
        >
          <ChevronLeft className={`w-3 h-3 transition-transform ${collapsed ? "rotate-180" : ""}`} />
        </button>
      </aside>

      {/* Mobile header — top bar with logo + currency toggle + drawer trigger */}
      <div
        className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#050505]/90 backdrop-blur-xl border-b border-white/[0.10] px-4 py-3 flex items-center justify-between"
        style={{ paddingTop: "max(12px, env(safe-area-inset-top))" }}
      >
        <div className="flex items-center gap-2">
          <NavLink to="/portfolio" onClick={() => setMobileOpen(false)} data-testid="mobile-logo-home" aria-label="Go to Portfolio home">
            <img src="/equia-logo.png" alt="EQUIA" className="h-9 w-auto" data-testid="mobile-equia-logo" />
          </NavLink>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="flex items-stretch bg-white/[0.02] border border-white/[0.12] p-0.5"
            data-testid="mobile-currency-switch"
          >
            {homeCurrencies.map((code) => (
              <button
                key={code}
                onClick={() => setMode(code)}
                data-testid={`mobile-currency-${code.toLowerCase()}`}
                className={`text-[10px] font-mono tracking-[0.08em] px-2 py-1 transition-all ${
                  mode === code ? "bg-[#D4AF37] text-black" : "text-white/40"
                }`}
                aria-label={`Set home currency to ${code}`}
              >
                {code}
              </button>
            ))}
          </div>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
            className="text-white/60 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="md:hidden fixed inset-0 bg-black/60 z-40"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: "spring", damping: 25 }}
              className="md:hidden fixed left-0 top-0 bottom-0 w-[260px] bg-[#050505] border-r border-white/[0.10] z-50"
            >
              <SidebarContent isMobile />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto pt-14 md:pt-0">
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto min-h-[calc(100vh-64px)]">
          {children}
        </div>
        {/* Footer */}
        <footer className="border-t border-white/[0.08] py-5 px-4 md:px-8" data-testid="app-footer">
          <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-white/15 text-xs font-sans">&copy; {new Date().getFullYear()} EQUIA Global Property</p>
            <div className="flex items-center gap-6">
              <a href="/legal" className="text-white/25 text-xs font-sans hover:text-[#D4AF37] transition-colors" data-testid="footer-legal-link">Legal Notice</a>
              <a href="/privacy" className="text-white/25 text-xs font-sans hover:text-[#D4AF37] transition-colors" data-testid="footer-privacy-link">Privacy Policy</a>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}
